
import React from 'react';

import { createToolbarEvent, sendAnalytics } from '../../../analytics';
import { setObjectFit } from '../../../base/conference';
import { openDialog } from '../../../base/dialog';
import { getWindowWidth } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n';
import {
    Icon,
    IconArrowLeft,
    IconArrowRight,
    IconCameraEmptyDisabled,
    IconContain,
    IconCover,
    IconCrown,
    IconDominantSpeaker,
    IconExitFullScreen,
    IconFullScreen,
    IconModeratorPin,
    IconNetwork,
    IconShareDesktop,
    IconUserPinned,
    IconZoomIn,
    IconZoomOut,
    PictureInPictureExitIcon,
    PictureInPictureInIcon
} from '../../../base/icons';
import { connect } from '../../../base/redux';
import { getBrandingColorPalette } from '../../../base/styles';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import { ConnectionIndicator } from '../../../connection-indicator';
import { setLargeVideoFullscreen } from '../../../large-video/actions.any';
import PresentationWindowButton from '../../../presentation-window/components/web/PresentationWindowButton';
import { setFullScreen, setLabelsVisible } from '../../../toolbox/actions.web';
import { AudioSettingsButton } from '../../../toolbox/components/web';
import { togglezoom, togglezoomout } from '../../../toolbox/functions.web';
import { LAYOUTS } from '../../../video-layout';
import { VideoQualityDialog } from '../../../video-quality';
import AbstractLabels, {
    type Props,
    _abstractMapDispatchToProps,
    _abstractMapStateToProps as _mapStateToProps
} from '../AbstractLabels';

import InsecureRoomNameLabel from './InsecureRoomNameLabel';

declare var interfaceConfig: Object;

/**
 * The type of the React {@code Component} state of {@link Labels}.
 */
type State = {

  /**
   * Whether or not the filmstrip was not visible but has transitioned in the
   * latest component update to visible. This boolean is used  to set a class
   * for position animations.
   *
   * @type {boolean}
   */
  filmstripBecomingVisible: boolean,
  viewStripMenu: boolean,
};

/**
 * A container to hold video status labels, including recording status and
 * current large video quality.
 *
 * @augments Component
 */
class Labels extends AbstractLabels<Props, State> {
    /**
   * Updates the state for whether or not the filmstrip is transitioning to
   * a displayed state.
   *
   * @inheritdoc
   */
    static getDerivedStateFromProps(props: Props, prevState: State) {
        return {
            filmstripBecomingVisible:
        !prevState.filmstripBecomingVisible && props._filmstripVisible
        };
    }

    /**
   * Initializes a new {@code Labels} instance.
   *
   * @param {Object} props - The read-only properties with which the new
   * instance is to be initialized.
   */
    constructor(props: Props) {
        super(props);

        this.state = {
            filmstripBecomingVisible: false,
            viewStripMenu: false,
            FeaturedIcon: undefined
        };
    }

    /**
   * Dispatches an action to open the video quality dialog.
   *
   * @private
   * @returns {void}
   */
    _doOpenVideoQuality() {
        this.props.dispatch(openDialog(VideoQualityDialog));
    }

    /**
   * Creates an analytics toolbar event and dispatches an action for toggling
   * open the video quality dialog.
   *
   * @private
   * @returns {void}
   */
    _onToolbarOpenVideoQuality() {
        sendAnalytics(createToolbarEvent('video.quality'));
        this._doOpenVideoQuality();
    }


    /**   * Toggles the object fit of the participant's video.
   *   * @private
   * @returns {void}
   */
    _onToggleObjectFit() {
        this.props.dispatch(
            setObjectFit(
                this.props.objectFit === 'cover' ? 'contain' : 'cover'
            )
        );

        // jitsiLocalStorage.setItem('objectFit', this.props.objectFit === 'cover' ? 'contain' : 'cover');
    }

    /**
   * Creates an analytics toolbar event and dispatches an action for toggling
   * full screen mode.
   *
   * @private
   * @returns {void}
   */
    _onToolbarToggleFullScreen() {
        sendAnalytics(
      createToolbarEvent('toggle.fullscreen', {
          enable: !this.props._fullScreen
      })
        );

        this._doToggleFullScreen();
    }


    /**
     * Function.
     */
    _doToggleFullScreen() {
        const fullScreen = !this.props._fullScreen;

        this.props.dispatch(setFullScreen(fullScreen));
    }

    handleConferenceSideBarView = () => {
        this.props.dispatch(setLabelsVisible(!this.props._showtoolbox));
    };

    handleLargeVideoFullScreen = () => {
        this.props.dispatch(setLargeVideoFullscreen(!this.props.largevideofullscreen));
        this.props.dispatch(setLabelsVisible(this.props.largevideofullscreen));
    };

    /**
   * Handles the click event for the zoom in button.
   * @private
   * @returns {void}
   */
    _onZoomIn() {
        this.props.dispatch(togglezoom(1)); // Pass 1 as zoom increment
        sendAnalytics(createToolbarEvent('video-zoom.zoomin'));
    }

    /**
     * return the tooltip content for the participant shared content
     * @returns tooltip value
     */
    _shareIndicatorTooltipContent() {
        const { isParticipantScreenSharing, isParticipantSharingEtherpad, isParticipantSharingWhiteboard, isSharingYoutubeVideo, isParticipantSharingAudio, t } = this.props;

        if (isParticipantScreenSharing) {
            return t('videothumbnail.userSharingScreen');
        } else if (isParticipantSharingWhiteboard) {
            return t('videothumbnail.userSharingWhiteBoard');
        } else if (isParticipantSharingEtherpad) {
            return t('videothumbnail.userSharingLivePad');
        } else if (isSharingYoutubeVideo) {
            return t('videothumbnail.userSharingYouTube');
        } else if (isParticipantSharingAudio) {
            return t('videothumbnail.userSharingScreenAudio');
        }

        return null;

    }

    /**
   * Handles the click event for the zoom out button.
   * @private
   * @returns {void}
   */
    _onZoomOut() {
        this.props.dispatch(togglezoomout(1)); // Pass 1 as zoom decrement
        sendAnalytics(createToolbarEvent('video-zoom.zoomout'));
    }


    /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
    render() {
        const {
            _videoTrack,
            _filmStripView,
            _isPictureInPictureVisible,
            onClickedLabel,
            _showVideoMutedIndicator,
            _conferenceView,
            _isModeratorPinned,
            _isDominantSpeakerDisabled,
            _participant,
            _connectionIndicatorDisabled,
            _visibleButtons,
            _connectionIndicatorAutoHideEnabled,
            isHovered,
            _showtoolbox,
            isParticipantScreenSharingTrack,
            hideHeaderFooter,
            _iAmRecorder,
            largevideofullscreen,
            renderModeratorIndicator,
            t
        } = this.props;
        const { VIDEO_QUALITY_LABEL_DISABLED } = interfaceConfig;
        const className = 'large-video-labels';

        // const labelButtonsStyle = largevideofullscreen || hideHeaderFooter ? {} : getWindowWidth() < 805 || window.innerHeight < 360 ? { flexDirection: 'column',
        //     bottom: 50 } : { flexDirection: 'row',
        //     right: 40 };
        const showDominantSpeaker = !_isDominantSpeakerDisabled && this.props._dominantSpeaker;
        const { id, local = false, pinned } = _participant || {};
        const pipStyle = { background: `${getBrandingColorPalette(interfaceConfig)?.accent5}80` };
        const showConnectionIndicator
            = isHovered || !_connectionIndicatorAutoHideEnabled;

        // Determine if PiP is supported (not supported in Firefox and Safari yet) & Should only work in Iframe & chromium based browsers for Latest PIP mode
        const isPiPSupported = Boolean(document.pictureInPictureEnabled)
            && Boolean(HTMLVideoElement.prototype.requestPictureInPicture)
                && (window.top === window.self) && (!_videoTrack?.muted || ('documentPictureInPicture' in window));

        return (
            <div className = { className }>
                <div className = 'large-video-labels-upper-parent'>
                    <div className = { `large-video-labels-left ${_showtoolbox ? 'visible' : ''}` }>
                        {(pinned || _isModeratorPinned) && (<Tooltip
                            content = { _isModeratorPinned ? t('videothumbnail.pinByModerator') : t('videothumbnail.userispinned') }
                            position = { 'bottom-left' }>
                            <div className = 'label_buttons pipstroke'>
                                <Icon
                                    color = { 'transparent' }
                                    src = { _isModeratorPinned ? IconModeratorPin : IconUserPinned }
                                    strokeColor = { getBrandingColorPalette(interfaceConfig)?.accent5 } />
                            </div>
                        </Tooltip>)}
                        {showDominantSpeaker && (
                            <Tooltip
                                content = { t('videothumbnail.dominentSpeakerTooltip') }
                                position = { 'bottom-left' }>
                                <div className = 'label_buttons pipstroke'>
                                    <Icon src = { IconDominantSpeaker } />
                                </div>
                            </Tooltip>
                        )}
                        {!_connectionIndicatorDisabled && (
                            <ConnectionIndicator
                                StatusIcon = { IconNetwork }
                                _connectionStatus = { _participant?.connectionStatus }
                                alwaysVisible = { showConnectionIndicator }
                                enableStatsDisplay = { true }
                                isLocalVideo = { local }
                                largeVideo = { true }
                                participantId = { id }
                                statsPopoverPosition = { 'right-start' } />
                        )}
                    </div>

                    {(<div
                        className = { `large-video-labels-upper ${_showtoolbox ? 'visible' : ''}` }>

                        {this.props._showVideoQualityLabel && !VIDEO_QUALITY_LABEL_DISABLED && (
                            <div>
                                {this._renderVideoQualityLabel()}
                            </div>
                        )}


                        {isPiPSupported && !_iAmRecorder && (
                            <Tooltip
                                content = {
                                    _isPictureInPictureVisible ? t('voicecommand.inActivePIPLabel') : t('voicecommand.activePIPLabel')
                                }
                                position = { 'bottom' }>
                                <button
                                    className = 'label_buttons pipstroke'
                                    onClick = { () => onClickedLabel(!_isPictureInPictureVisible) }
                                    style = { pipStyle }>
                                    <div className = 'video_space-icon pipstroke picture-in-picture-icon'>
                                        <Icon
                                            color = { 'transparent' }
                                            src = {
                                                _isPictureInPictureVisible
                                                    ? PictureInPictureExitIcon
                                                    : PictureInPictureInIcon
                                            }
                                            strokeColor = { getBrandingColorPalette(interfaceConfig)?.accent5 }
                                            width = { 20 } />
                                    </div>
                                    {/* <p>{t('toolbar.pip')}</p> */}
                                </button>
                            </Tooltip>
                        )}
                        {_showVideoMutedIndicator && _filmStripView !== LAYOUTS.TILE_VIEW ? (

                            <Tooltip
                                content = { t('videothumbnail.videoMuted') }
                                position = { 'bottom-right' }>
                                <div className = 'label_buttons'>
                                    <Icon src = { IconCameraEmptyDisabled } />
                                </div>
                            </Tooltip>
                        ) : null}
                        {
                            this._shareIndicatorTooltipContent() !== null && (<Tooltip
                                content = { this._shareIndicatorTooltipContent() }
                                position = { 'bottom-right' }>
                                <button
                                    className = 'label_buttons'>
                                    <Icon
                                        src = { IconShareDesktop }
                                        width = { 20 } />
                                </button>
                            </Tooltip>)
                        }
                        {
                            renderModeratorIndicator && (<Tooltip
                                content = { t('videothumbnail.moderator') }
                                position = { 'bottom-right' }>
                                <button
                                    className = 'label_buttons'>
                                    <Icon
                                        src = { IconCrown }
                                        width = { 20 } />
                                </button>
                            </Tooltip>)
                        }
                    </div>)}
                </div>
                <div className = { `large-video-labels-bottom ${_showtoolbox ? 'visible' : ''}` }>
                    {_filmStripView !== LAYOUTS.TILE_VIEW && !_showVideoMutedIndicator && !_iAmRecorder && _conferenceView !== 'float' && (
                        <Tooltip
                            content = { t('toolbar.zoomin') }
                            position = { 'left' }>
                            <button
                                className = 'label_buttons'
                                onClick = { () => this._onZoomIn() }>
                                <Icon
                                    src = { IconZoomIn }
                                    width = { 20 } />
                            </button>
                        </Tooltip>)}
                    {_filmStripView !== LAYOUTS.TILE_VIEW && !_showVideoMutedIndicator && !_iAmRecorder && _conferenceView !== 'float' && _visibleButtons.has('zoomout') && (<Tooltip
                        content = { t('toolbar.zoomout') }
                        position = { 'left' }>
                        <button
                            className = 'label_buttons'
                            onClick = { () => this._onZoomOut() }>
                            <Icon
                                src = { IconZoomOut }
                                width = { 20 } />
                        </button>
                    </Tooltip>)}
                    {_filmStripView !== LAYOUTS.TILE_VIEW && !_showVideoMutedIndicator && !_iAmRecorder && _conferenceView !== 'float'
                        && _participant?.isFakeParticipant !== 'RemoteScreenShare' && _participant?.isFakeParticipant !== 'LocalScreenShare' && _visibleButtons.has('fit-video') && (<Tooltip
                        content = { this.props.objectFit === 'cover' ? t('tooltip.switchToContain') : t('tooltip.switchToCover') }
                        position = { 'left' }>
                        <button
                            className = 'label_buttons'
                            onClick = { () => this._onToggleObjectFit() }>
                            <Icon
                                src = { this.props.objectFit === 'cover' ? IconCover : IconContain } // Use FixedIcon for 'cover' and Icon for 'contain'
                                width = { 20 } />
                        </button>
                    </Tooltip>)}
                    {_filmStripView !== LAYOUTS.TILE_VIEW && !_showVideoMutedIndicator && !_iAmRecorder && _conferenceView !== 'float' && isParticipantScreenSharingTrack
                        && <PresentationWindowButton />}

                    {_filmStripView !== LAYOUTS.TILE_VIEW && !_iAmRecorder && _conferenceView !== 'float' && getWindowWidth() > 767 && (
                        <div
                            className = { `${_showtoolbox ? '' : 'large-video-labels-video-options'}` }>
                            <Tooltip
                                content = { _showtoolbox ? t('tooltip.hideVideoOptions') : t('tooltip.showVideoOptions') }
                                position = { 'left' }>
                                <button
                                    className = 'label_buttons toggle_labels'
                                    onClick = { () => this.handleConferenceSideBarView() }>
                                    <Icon
                                        src = { _showtoolbox ? IconArrowRight : IconArrowLeft }
                                        width = { 20 } />
                                </button>
                            </Tooltip>
                        </div>
                    )}
                    {_filmStripView !== LAYOUTS.TILE_VIEW && !_iAmRecorder && (
                        <div
                            className = { `${_showtoolbox ? '' : 'large-video-labels-full-screen'}` }>
                            <Tooltip
                                content = { largevideofullscreen ? t('tooltip.hideVideoOptions') : t('tooltip.showVideoOptions') }
                                position = { 'left' }>
                                <button
                                    className = { `label_buttons ${largevideofullscreen ? 'largevideo-exit-full-screen' : 'largevideo-full-screen'}` }
                                    onClick = { () => this.handleLargeVideoFullScreen() }>
                                    <Icon
                                        src = { largevideofullscreen ? IconExitFullScreen : IconFullScreen }
                                        width = { 20 } />
                                </button>
                            </Tooltip>
                        </div>
                    )}
                </div>

                {_filmStripView !== LAYOUTS.TILE_VIEW && !_showtoolbox && !_iAmRecorder && _conferenceView !== 'float' && getWindowWidth() > 767 && (
                    <div
                        className = { `${_showtoolbox ? '' : 'large-video-labels-video-options'}` }>
                        <Tooltip
                            content = { _showtoolbox ? t('tooltip.hideVideoOptions') : t('tooltip.showVideoOptions') }
                            position = { 'left' }>
                            <button
                                className = 'label_buttons toggle_labels'
                                onClick = { () => this.handleConferenceSideBarView() }>
                                <Icon
                                    src = { _showtoolbox ? IconArrowRight : IconArrowLeft }
                                    width = { 20 } />
                            </button>
                        </Tooltip>
                    </div>
                )}
                {_filmStripView !== LAYOUTS.TILE_VIEW && !_showtoolbox && !_iAmRecorder && _conferenceView !== 'float' && (
                    <div
                        className = { `${_showtoolbox ? '' : 'large-video-labels-full-screen'}` }>
                        <Tooltip
                            content = { largevideofullscreen ? t('tooltip.hideVideoOptions') : t('tooltip.showVideoOptions') }
                            position = { 'left' }>
                            <button
                                className = { `label_buttons ${largevideofullscreen ? 'largevideo-exit-full-screen' : 'largevideo-full-screen'}` }
                                onClick = { () => this.handleLargeVideoFullScreen() }>
                                <Icon
                                    src = { largevideofullscreen ? IconExitFullScreen : IconFullScreen }
                                    width = { 20 } />
                            </button>
                        </Tooltip>
                    </div>
                )}
                {(largevideofullscreen || hideHeaderFooter) && <div
                    className = { getWindowWidth() < 500 ? 'audio-btn-largevideo-small' : 'audio-btn-largevideo-large' }>
                    <div className = 'icon-btn'>
                        <AudioSettingsButton
                            dispatch = { this.props.dispatch }
                            hidepopup = { true }
                            key = 'asb'
                            visible = { false } />
                    </div>
                </div>}
            </div>
        );
    }

    _renderE2EELabel: () => React$Element<*>;

    _renderLocalRecordingLabel: () => React$Element<*>;

    _renderRecordingLabel: (string) => React$Element<*>;

    _renderTranscribingLabel: () => React$Element<*>;

    /**
     * Renders the {@code InsecureRoomNameLabel}.
     *
     * @protected
     * @returns {React$Element}
     */
    _renderInsecureRoomNameLabel() {
        return <InsecureRoomNameLabel />;
    }

    _renderVideoQualityLabel: () => React$Element<*>;
}

export default connect(_mapStateToProps, _abstractMapDispatchToProps)(translate(Labels));
